import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import numeral from 'numeral';

import AddressInput2  from 'app/components/common/address-input2';
import DatePicker     from 'app/components/common/date-picker';
import FileInput      from 'app/components/common/file-input';
import Icon           from 'app/components/common/icon';
import NumberInput    from 'app/components/common/number-input';
import MultiSelect    from 'app/components/common/multi-select';
import NonprofitInput from 'app/components/common/nonprofit-input';
import PhoneInput     from 'app/components/common/phone-input';
import RadioSelect    from 'app/components/common/radio-select';
import RteEditor      from 'app/components/common/rich-text/editor';
import RteViewer      from 'app/components/common/rich-text/viewer';
import StandardInput  from 'app/components/common/standard-input';
import StandardSelect from 'app/components/common/standard-select';
import formsHelpers   from 'app/helpers/forms';
import utils          from 'app/helpers/utils';

const {FieldTypes, typeObjMap} = formsHelpers;

class FormField extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      value: props.answer?.value || {},
    };

    this.onChangeTextShort = this.onChangeTextShort.bind(this);
    this.onChangeTextLong = this.onChangeTextLong.bind(this);
    this.onChangeDate = this.onChangeDate.bind(this);
    this.onChangeNumber = this.onChangeNumber.bind(this);
    this.onChangeCurrency = this.onChangeCurrency.bind(this);
    this.onChangeNonprofit = this.onChangeNonprofit.bind(this);
    this.onChangeSelect = this.onChangeSelect.bind(this);
    this.onChangeMultiSelect = this.onChangeMultiSelect.bind(this);
    this.onChangePhone = this.onChangePhone.bind(this);
    this.onChangeAddress = this.onChangeAddress.bind(this);
    this.onChangeFiles = this.onChangeFiles.bind(this);
  }

  componentDidMount() {
    this.props.onAnswer(this.props.field, this.answer);
  }

  get type() {
    return this.props.field.type;
  }

  get answer() {
    const {field} = this.props;
    return {
      formFieldId: this.props.field.id,
      value: {
        [this.type]: this.rawValue,
      },
      fieldConfig: {
        type: field.type,
        title: field.title,
        opts: field.opts,
        isRequired: field.isRequired,
      },
    };
  }

  get rawValue() {
    const rv = this.state.value[this.type];
    if (rv == null) return null; // coerce undefined into null
    return rv;
  }
  set rawValue(val) {
    this.setState({value: {[this.type]: val}}, () => {
      this.props.onAnswer(this.props.field, this.answer);
    });
  }

  // TEXT_SHORT
  onChangeTextShort(event, strVal) {
    this.rawValue = strVal;
  }
  renderInputTextShort() {
    const {maxChars} = this.props.field.opts;
    return <StandardInput name="field" label="Answer" onChange={this.onChangeTextShort} value={this.rawValue || ''} maxChars={maxChars} />;
  }

  // TEXT_LONG
  onChangeTextLong(md) {
    this.rawValue = md;
  }
  renderInputTextLong() {
    const {maxWords} = this.props.field.opts;
    return <RteEditor initialMd={this.rawValue || ''} onChange={this.onChangeTextLong} name="field" placeholder="Answer" hideTextBlocks maxWords={maxWords} />;
  }

  // DATE
  onChangeDate(dateStr) {
    this.rawValue = dateStr;
  }
  renderInputDate() {
    return <DatePicker allowClear onChange={this.onChangeDate} dateStr={this.rawValue} />
  }

  // NUMBER
  onChangeNumber(numVal) {
    this.rawValue = numVal;
  }
  renderInputNumber() {
    const {min, max} = this.props.field.opts;
    const label = (() => {
      let str = 'Number';
      const parens = [];
      const formatNum = (num) => numeral(num).format('0,0[.][000]');
      if (min) parens.push(`Min: ${formatNum(min)}`);
      if (_.isFinite(max)) parens.push(`Max: ${formatNum(max)}`);
      if (parens.length) str += ` (${parens.join(' | ')})`;
      return str;
    })();
    return <NumberInput value={this.rawValue} onChange={this.onChangeNumber} decPlaces={3} min={min} max={max} label={label} />;
  }

  // MONETARY_AMOUNT
  onChangeCurrency(amount) {
    this.rawValue = {amount, currencyCode: 'USD'};
  }
  renderInputMonetary() {
    const {min, max} = this.props.field.opts;
    const amount = _.isFinite(this.rawValue?.amount) ? this.rawValue.amount : null;
    const label = (() => {
      let str = 'Dollar Amount';
      const parens = [];
      const formatNum = (num) => numeral(num).format('$0,0.00');
      if (min) parens.push(`Min: ${formatNum(min)}`);
      if (_.isFinite(max)) parens.push(`Max: ${formatNum(max)}`);
      if (parens.length) str += ` (${parens.join(' | ')})`;
      return str;
    })();
    return <NumberInput value={amount} onChange={this.onChangeCurrency} decPlaces={2} min={min} max={max} label={label} preciseZeros icon={<Icon.CurrencyDollar />} />;
  }

  // NONPROFIT
  onChangeNonprofit(nonprofit) {
    this.rawValue = nonprofit?.id || null;
  }
  renderInputNonprofit() {
    return <NonprofitInput.ById name="field" onChange={this.onChangeNonprofit} id={this.rawValue} allowClear />;
  }

  // SELECT
  onChangeSelect(value) {
    this.rawValue = value;
  }
  renderInputSelect() {
    const {field} = this.props;
    const opts = (field.opts.options || []).map(o => ({label: o, value: o}));
    const isDropdown = field.opts.displayAs !== 'list';
    return isDropdown
      ? <StandardSelect options={opts} onSelect={this.onChangeSelect} value={this.rawValue} allowClear />
      : <RadioSelect options={opts} onSelect={this.onChangeSelect} value={this.rawValue} allowClear />;
  }

  // MULTI SELECT
  onChangeMultiSelect(value) {
    this.rawValue = value;
  }
  renderInputMultiSelect() {
    const {field} = this.props;
    const opts = (field.opts.options || []).map(o => ({label: o, value: o}));
    const isDropdown = field.opts.displayAs !== 'list';
    return isDropdown
      ? <MultiSelect options={opts} onSelect={this.onChangeMultiSelect} value={this.rawValue || []} />
      : <RadioSelect options={opts} onSelect={this.onChangeMultiSelect} value={this.rawValue || []} isMulti />;
  }

  // PHONE
  onChangePhone(phoneNumber) {
    this.rawValue = phoneNumber || null;
  }
  renderInputPhone() {
    return <PhoneInput phone={this.rawValue} onChange={this.onChangePhone} />;
  }

  // ADDRESS
  onChangeAddress(address) {
    this.rawValue = address;
  }
  renderInputAddress() {
    return <AddressInput2 address={this.rawValue} onChange={this.onChangeAddress} />;
  }

  // FILE
  onChangeFiles(files) {
    this.rawValue = files;
  }
  renderInputFile() {
    const {field} = this.props;
    const {types, isMulti} = field.opts;
    return <FileInput maxFiles={isMulti ? 10 : 1} files={this.rawValue || []} onChange={this.onChangeFiles} types={types || []} />;
  }

  renderInput() {
    if (this.type === FieldTypes.TEXT_SHORT)      return this.renderInputTextShort();
    if (this.type === FieldTypes.TEXT_LONG)       return this.renderInputTextLong();
    if (this.type === FieldTypes.DATE)            return this.renderInputDate();
    if (this.type === FieldTypes.NUMBER)          return this.renderInputNumber();
    if (this.type === FieldTypes.MONETARY_AMOUNT) return this.renderInputMonetary();
    if (this.type === FieldTypes.NONPROFIT)       return this.renderInputNonprofit();
    if (this.type === FieldTypes.SELECT)          return this.renderInputSelect();
    if (this.type === FieldTypes.MULTI_SELECT)    return this.renderInputMultiSelect();
    if (this.type === FieldTypes.PHONE)           return this.renderInputPhone();
    if (this.type === FieldTypes.ADDRESS)         return this.renderInputAddress();
    if (this.type === FieldTypes.FILE)            return this.renderInputFile();
    return null;
  }

  render() {
    const {field} = this.props;
    if (!field) return null;
    if (field._delete || field.deletedAt) return null;

    return (
      <div id={`field-${field.id}`} className="form-field">
        <h2 className="bform-h2 form-field-title">{field.title}{field.isRequired ? '*' : ''}</h2>
        <RteViewer md={field.description} fontSize="normal" />
        {this.renderInput()}
        {/* <pre>{JSON.stringify({rawValue: this.rawValue})}</pre> */}
        {/* <pre>{JSON.stringify({propsAnswer: this.props.answer},null,4)}</pre> */}
      </div>
    );
  }

}


FormField.propTypes = {
  field: PropTypes.object.isRequired,
  answer: PropTypes.object,
  editMode: PropTypes.bool,
  previewMode: PropTypes.bool,
  onAnswer: PropTypes.func,
};

FormField.defaultProps = {
  editMode: false,
  previewMode: false,
  onAnswer: utils.noop,
};

export default FormField;
